import { useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { PublicSourceAnalyticsSchedule, SourceScheduleInput } from "../../../../shared/types";
import { useSaveSourceAction, useTurnOnDailyRefresh } from "../../hooks/useSourceSchedules";
import { defaultSourceSchedule } from "../../lib/source-schedule-defaults";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { OperatorSurface } from "../ui/OperatorSurface";
import { ScheduleFields } from "../ui/ScheduleFields";
import { SourceHistoryImport } from "./SourceHistoryImport";

/** Blockers the owner resolves on the account connection (reauthorize, rediscover, reassign). */
const CONNECTION_FIXES = new Set(["reconnect_source", "resource_unavailable", "assignment_unavailable"]);

/**
 * The last step of connecting an analytics source: a connected, supported source with no
 * schedule collects nothing (source schedules are the only collector), so wherever such a
 * source is shown it gets this panel instead of a quiet "Not scheduled". The owner picks the
 * calendar (the shared default) and turns refresh on explicitly; nothing is enabled for them.
 * When the source cannot be scheduled yet, the panel names why and links to the fix rather
 * than rendering a control that cannot succeed.
 */
export function DailyRefreshSetup({ source, brandId, warnings, openAction }: {
  source: PublicSourceAnalyticsSchedule; brandId: string; warnings?: ReactNode; openAction?: ReactNode;
}) {
  const id = useId();
  const [draft, setDraft] = useState<SourceScheduleInput>(() => defaultSourceSchedule());
  const [validation, setValidation] = useState("");
  const turnOn = useTurnOnDailyRefresh(brandId, source);
  const saveAction = useSaveSourceAction(brandId, source);
  const blocked = source.blockers.length > 0;
  const fixInConnections = source.blockers.some(reason => CONNECTION_FIXES.has(reason));
  // Without a scheduling worker the calendar cannot be saved, but the collection Action can.
  const actionOnly = blocked && source.action_saved === false && source.blockers.every(reason => reason === "source_scheduler_unavailable");
  return <OperatorSurface as="div" density="compact" className="border-amber-300">
    <section aria-labelledby={`${id}-title`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="font-semibold text-slate-900">Automatic refresh</h3>
          <p id={`${id}-title`} className="mt-1 text-base font-semibold text-slate-950">Finish setup: turn on daily refresh</p></div>
        <Badge variant="warning">{blocked ? "Blocked" : "Not set up"}</Badge>
      </div>
      <p id={`${id}-help`} className="mt-2 text-sm text-slate-700">This source is connected, but it has no refresh schedule, so no new data is collected automatically.
        {blocked ? " Resolve the issue below, then turn on daily refresh." : " Choose when it refreshes and turn it on. Collection then follows this calendar; pausing later keeps saved data."}</p>
      {warnings}
      {blocked ? <div className="mt-3 space-y-2">
        {fixInConnections && <Link to={`/settings/connections/${encodeURIComponent(source.connection_id)}?brandId=${encodeURIComponent(brandId)}#refresh-${encodeURIComponent(source.assignment_id)}`}
          className="inline-block text-sm font-medium text-brand-700 underline">Fix in Connections</Link>}
        {actionOnly && <div className="space-y-2"><p className="text-sm text-slate-600">Save this source’s collection Action now; turn on daily refresh once scheduling is available.</p>
          <Button size="sm" variant="secondary" loading={saveAction.isPending} onClick={() => saveAction.mutate()}>Save collection Action</Button>
          {saveAction.error && <p role="alert" className="text-xs text-red-700">{saveAction.error.message}</p>}</div>}
      </div> : <form className="mt-3 space-y-3" aria-describedby={`${id}-help`} onSubmit={event => {
        event.preventDefault();
        try { new Intl.DateTimeFormat(undefined, { timeZone: draft.timezone }).format(); }
        catch { setValidation("Enter a valid IANA timezone, such as America/Chicago."); return; }
        setValidation("");
        turnOn.mutate(draft);
      }}>
        <ScheduleFields value={draft} onChange={setDraft} disabled={turnOn.isPending} />
        <Button size="sm" type="submit" loading={turnOn.isPending}>Turn on daily refresh</Button>
      </form>}
      {openAction}
      {turnOn.isPending && <p role="status" className="mt-3 text-xs">Saving schedule and turning on refresh…</p>}
      {(validation || turnOn.error) && <p role="alert" className="mt-3 text-xs text-red-700">{validation || `${turnOn.error?.message} Daily refresh was not confirmed; review the reloaded status before retrying.`}</p>}
      {source.history && <div className="mt-4 border-t border-slate-100 pt-3">
        <p className="text-xs text-slate-600">Want earlier data too? Import history and turn on refresh in one step instead.</p>
        <SourceHistoryImport source={source} brandId={brandId} />
      </div>}
    </section>
  </OperatorSurface>;
}
